import { useCallback, useMemo } from 'react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Download, Eye, Info, Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEcho } from '@laravel/echo-react';
import { filesize } from 'filesize';
import {
  ActivateVersionMenuItem,
  DeleteVersionMenuItem,
  ViewVersionMenuItem,
} from '@/components/menu-items/node-menu-items';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { MenuProvider, MenuSeparator, dropdownPrimitives } from '@/components/menu-renderer';
import { OptionAction } from '@/components/inspector/entity-fields/form/option-action';
import { OptionItem } from '@/components/inspector/entity-fields/form/option-item';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Options } from '@/components/inspector/entity-fields/form/options';
import { AssetPreviewContent } from '@/components/asset-preview-content';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { Separator } from '@/components/ui/separator';
import { type Asset, Variants } from '@/types/asset';
import { useDownload } from '@/hooks/useDownload';
import { FileType } from '@/types/fileType';
import { QueryKey } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

const AssetVersionCreatedListener = ({
  assetId,
  onVersionCreated,
}: {
  assetId: string;
  onVersionCreated: (event: { assetId?: string; versionId?: string }) => void;
}) => {
  const channelName = useMemo(() => `asset.${assetId}`, [assetId]);
  useEcho(channelName, '.asset.version.created', onVersionCreated, [channelName, onVersionCreated]);

  return null;
};

export const VersionsAccordion = ({
  asset,
  disabled,
  readOnly = false,
}: {
  asset: Asset;
  disabled: boolean;
  readOnly?: boolean;
}) => {
  const queryClient = useQueryClient();
  const assetId = asset?.id;

  const tCommon = useTranslations('common');
  const tVersions = useTranslations('inspector.versions');
  const tLabels = useTranslations('labels.version');
  const tDownloads = useTranslations('downloads');
  const openModal = useOpenModal();

  const download = useDownload({
    title: tDownloads('assetVersionDownloadingTitle'),
    description: tDownloads('assetVersionDownloadStartedDescription'),
  });

  const handleVersionCreated = useCallback(
    (event: { assetId?: string; versionId?: string }) => {
      if (!assetId) {
        return;
      }

      if (event?.assetId && event.assetId !== assetId) {
        return;
      }

      void queryClient.invalidateQueries({ queryKey: [QueryKey.Asset, assetId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.AssetData] });
    },
    [assetId, queryClient],
  );

  return (
    <div className="flex h-full flex-col">
      {assetId && <AssetVersionCreatedListener assetId={assetId} onVersionCreated={handleVersionCreated} />}
      <Options
        disabled={disabled}
        handleAddNewOption={() => assetId && openModal(ModalId.UploadAssetVersion, { id: assetId })}
        addButtonPosition="top"
        className="h-full gap-y-5 py-2"
        readOnly={readOnly}
        fullHeight
        emptyState={
          <p className="px-4 py-12 text-center text-balance text-icon-base">
            {tVersions('empty')}{' '}
            {!readOnly && (
              <>
                <br />
                {tVersions.rich('addVersionHint', {
                  plusIcon: () => (
                    <span className="whitespace-nowrap">
                      {`"`}
                      <Plus className="inline icon-4" />
                      {`"`}
                    </span>
                  ),
                })}
              </>
            )}
          </p>
        }
      >
        {asset?.versions &&
          [...asset.versions].reverse().map((assetVersion) => {
            const { id, active, fileSize, width, height, fileType, originalFileName, thumbnail, preview, url } =
              assetVersion;

            return (
              <OptionItem optionKey={id} key={id}>
                <div key={id} className="flex w-full items-center gap-2">
                  <div className="flex grow items-center gap-3">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            'rounded-full icon-2',
                            active
                              ? 'bg-green-300 shadow-[0_0_5px_1px_rgb(134,239,172,1)]'
                              : 'bg-neutral-300 dark:bg-neutral-700',
                          )}
                        />
                      </TooltipTrigger>
                      <TooltipPortal>
                        <TooltipContent>{active ? tLabels('current') : tLabels('inactive')}</TooltipContent>
                      </TooltipPortal>
                    </Tooltip>

                    <div>
                      <div key={id} className="line-clamp-2 text-sm break-all">
                        {originalFileName}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-x-1">
                    <Popover>
                      <PopoverTrigger asChild>
                        <OptionAction
                          icon={Info}
                          data-cy={`option-action-button-variant-preview-${id}`}
                          tooltip={tCommon('viewDetails')}
                        />
                      </PopoverTrigger>
                      <PopoverContent>
                        <div className="flex flex-col space-y-2">
                          <div className="flex items-center justify-start gap-2">
                            <p
                              className={cn(
                                'inline rounded-xs px-1.5 py-0.5 text-xs uppercase leading-none',
                                fileType === FileType.Image
                                  ? 'bg-folder-background-color text-folder-color'
                                  : 'bg-neutral-100 text-neutral-400 dark:bg-neutral-800',
                              )}
                            >
                              {fileType}
                            </p>
                          </div>
                          <p className="max-w-64 text-sm font-medium">{originalFileName}</p>
                          <Separator />
                          <div className="flex flex-col gap-2">
                            {fileSize && (
                              <div className="flex items-center gap-2">
                                <p className="w-1/4 text-right text-xs leading-none text-neutral-500">
                                  {tVersions('fileSizeLabel')}
                                </p>
                                <div className="text-xs leading-none font-bold text-text-base">
                                  {filesize(fileSize)}
                                </div>
                              </div>
                            )}
                            {Boolean(width) && (
                              <div className="flex items-center gap-2">
                                <p className="w-1/4 text-right text-xs leading-none text-neutral-500">
                                  {tVersions('widthLabel')}
                                </p>
                                <div className="text-xs leading-none font-bold text-text-base">{width} px</div>
                              </div>
                            )}
                            {Boolean(height) && (
                              <div className="flex items-center gap-2">
                                <p className="w-1/4 text-right text-xs leading-none text-neutral-500">
                                  {tVersions('heightLabel')}
                                </p>
                                <div className="text-xs leading-none font-bold text-text-base">{height} px</div>
                              </div>
                            )}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>

                    {active || readOnly ? (
                      (fileType === FileType.Image || fileType === FileType.Video) && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <OptionAction
                              icon={Eye}
                              data-cy={`option-action-button-variant-preview-${id}`}
                              tooltip={tCommon('preview')}
                            />
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-(--breakpoint-lg)">
                            <AssetPreviewContent
                              // `asset.variants` only ever reflects the currently active version, so
                              // it must not be passed for an inactive row (read-only mode renders this
                              // same dialog for every row) — that would silently show the active
                              // version's image instead of the one the viewer clicked on.
                              asset={active ? asset : undefined}
                              name={originalFileName}
                              type={fileType}
                              // Active row: `asset` is passed, so `variantKey` resolves the
                              // preview-internal variant and wins. Inactive row (read-only/share
                              // renders this same dialog for every row): no `asset`, so the source
                              // is this version's own `preview` URL, falling back to `thumbnail`.
                              thumbnail={preview ?? thumbnail}
                              variantKey={Variants.PreviewInternal}
                              renderVideoPlayer={fileType === FileType.Video}
                            />
                          </DialogContent>
                        </Dialog>
                      )
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <OptionAction
                            icon={EllipsisVerticalIcon}
                            data-cy={`option-action-button-variant-more-options-${id}`}
                            tooltip={tCommon('moreOptions')}
                          />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <MenuProvider primitives={dropdownPrimitives}>
                            <ActivateVersionMenuItem asset={asset} versionId={id} />
                            {assetVersion && (fileType === FileType.Image || fileType === FileType.Video) && (
                              <ViewVersionMenuItem assetVersion={assetVersion} />
                            )}
                            <MenuSeparator />
                            <DeleteVersionMenuItem assetId={asset.id} versionId={id} danger />
                          </MenuProvider>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    <OptionAction
                      icon={Download}
                      disabled={!url}
                      {...(url && {
                        onClick: () => {
                          download(url, originalFileName).catch(() => {});
                        },
                      })}
                      data-cy={`option-action-button-variant-download-${id}`}
                      tooltip={tCommon('download')}
                    />
                  </div>
                </div>
              </OptionItem>
            );
          })}
      </Options>
    </div>
  );
};
